<template>
    <div class="handover-overlay">
        <template v-if="waitingForAgent">
            <user-headset class="handover-overlay__icon" />
            {{ t('waitForAgent') }}
            <timer
                v-if="assistent?.handoverSettings.delayInMinutes"
                :time-in-seconds="calculateTimeInSeconds"
                @timer:finished="waitingForAgent = false"
            />
            <button
                v-if="contactMeLaterEnabled"
                type="button"
                class="handover-overlay__block-button"
                @click="waitingForAgent = false"
            >
                {{ t('contactMeLater') }}
            </button>
            <button
                type="button"
                class="handover-overlay__cancel-button"
                @click="chat.cancelHandover()"
            >
                {{ t('cancel') }}
            </button>
        </template>

        <template v-else>
            <triangle-exclamation class="handover-overlay__icon" />
            {{ t('agentNotPresent') }}

            <template
                v-if="contactMeLaterEnabled"
            >
                <strong style="{ width: 100%; text-align: left }">
                    {{ t('contactUser') }}
                </strong>
                <div
                    v-for="field in assistent?.handoverSettings.fieldsToAskOnContactLater"
                    :key="field"
                    class="user-contact"
                >
                    <label :for="`field-${field}`">
                        {{ handoverFieldLabels[field] ?? field }}
                    </label>
                    <input
                        v-model.trim="localForm[field]"
                        class="user-contact__input"
                        :placeholder="handoverFieldLabels[field] ?? field"
                        type="text"
                    />
                </div>
                <button
                    type="button"
                    class="handover-overlay__block-button"
                    :disabled="!isFormValid"
                    @click="chat.contactMeLater(localForm)"
                >
                    {{ t('send') }}
                </button>
            </template>

            <button
                type="button"
                class="handover-overlay__cancel-button"
                @click="chat.cancelHandover()"
            >
                {{ t('cancel') }}
            </button>
        </template>
    </div>
</template>

<script setup lang="ts">
    import useConfiguration from '@/composables/useConfiguration';
    import { useI18n } from 'vue-i18n';
    import messages from './HandoverOverlay.messages.json';
    import { computed, onMounted, ref } from 'vue';
    import Timer from '@/components/CountdownTimer.vue';
    import UserHeadset from '@keyvue/one-awesome-icons/icons/regular/user-headset.svg?component';
    import TriangleExclamation from '@keyvue/one-awesome-icons/icons/regular/triangle-exclamation.svg?component';
    import { HandoverContactDataField } from '@keyvue/one-api-client';
    import Chat from '@/chat/Chat';
    import { object, string, type StringSchema } from 'yup';

    const chat = Chat.getInstance();

    const { t } = useI18n({ messages });
    const { assistent } = useConfiguration();

    const waitingForAgent = ref<boolean>(true);
    const localForm = ref<Form>({
        [HandoverContactDataField.NAME.value]: '',
        [HandoverContactDataField.ADDRESS.value]: '',
        [HandoverContactDataField.PHONE_NUMBER.value]: '',
        [HandoverContactDataField.EMAIL.value]: '',
        [HandoverContactDataField.NOTES.value]: '',
    });

    const handoverFieldLabels: Record<string, string> = {
        [HandoverContactDataField.NAME.value]: 'Naam',
        [HandoverContactDataField.ADDRESS.value]: 'Adres',
        [HandoverContactDataField.PHONE_NUMBER.value]: 'Telefoonnummer',
        [HandoverContactDataField.EMAIL.value]: 'E-mail',
        [HandoverContactDataField.NOTES.value]: 'Opmerkingen',
    };

    const calculateTimeInSeconds = computed((): number => {
        return chat.handoverSecondsLeft;
    });

    interface Form {
        Naam?: string;
        Adres?: string;
        Telefoonnummer?: string;
        'E-mail'?: string;
        Opmerkingen?: string;
    }

    const schema = computed(() => {
        const fields = assistent.value?.handoverSettings?.fieldsToAskOnContactLater ?? [];

        const shape: Record<string, StringSchema> = {};
        fields.forEach((field) => {
            switch (field) {
                case HandoverContactDataField.NAME.value:
                case HandoverContactDataField.ADDRESS.value:
                case HandoverContactDataField.NOTES.value:
                    shape[field] = string().trim();
                    break;
                case HandoverContactDataField.EMAIL.value:
                    shape[field] = string().trim().email('Ongeldig e-mailadres');
                    break;
                case HandoverContactDataField.PHONE_NUMBER.value:
                    shape[field] = string()
                        .trim()
                        .matches(/^\d+$/, 'Alleen cijfers toegestaan')
                        .min(8, 'Minimaal 8 cijfers');
                    break;
                default:
                    shape[field] = string().trim();
            }
        });

        return object()
            .shape(shape)
            .test(
                'Minstens 1 handover veld',
                'Vul minstens één veld in.',
                (value) => Object.values(value ?? {}).some((v) => v?.trim()),
            );
    });

    const contactMeLaterEnabled = computed(() => (
        !!assistent.value?.handoverSettings.contactMeLaterEnabled
    ));

    const isFormValid = computed(() => {
        try {
            schema.value.validateSync(localForm.value, { abortEarly: false });
            return true;
        } catch {
            return false;
        }
    });

    onMounted(() => {
        const fields = assistent.value?.handoverSettings.fieldsToAskOnContactLater ?? [];
        fields.forEach((field: string) => {
            localForm.value[field] = '';
        });
    });
</script>

<style scoped lang="scss">
    .handover-overlay {
        background-color: white;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 1.5rem 1.5rem;
        gap: .8rem;
        display: flex;
        flex-direction: column;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        border-top: solid 1px lightgray;
        transition: transform 200ms ease-in-out;
        transform: translateY(0);
        overflow-y: scroll;
        max-height: 100%;
        align-items: center;

        &__icon {
            color: #E8841E;
            width: 25px;
            align-self: center;
        }

        &__block-button {
            width: 100%;
            background-color: var(--color-balloon);
            color: var(--color-widget-text);
            font-weight: 500;
            font-size: 14px;
            padding: 12px 16px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            text-align: center;
            display: inline-block;
            transition: background-color 0.3s;

            &:hover {
                filter: brightness(1.1);
            }

            &:disabled {
                background-color: #ccc;
                color: #666;
                cursor: not-allowed;
            }
        }

        &__cancel-button {
            background-color: unset;
            color: #666666;
            font-weight: 400;
            font-size: 15px;
            padding: 12px 16px;
            border: none;
            cursor: pointer;
            text-align: center;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .user-contact {
        width: 100%;

        &__input {
            width: 100%;
            height: 40px;
            padding: 5px;

            border-radius: 8px;
            border: 2px solid lightgrey;
        }
    }
</style>
